import { type JSX,useCallback } from 'react';

import { Box, Stack } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ClaimValueField } from 'common/components/sharedCustomComponents/Claims/ClaimValueField';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { useClaimMembershipDrawer } from 'common/hooks/useClaimMembershipDrawer';
import { APIRoleClaimMembershipPostParams, APIRoleClaimMembershipPutParams } from 'common/services/api/RoleClaimMembership/RoleClaimMembership.api';
import {
    postRoleClaimMembership,
    PostRoleClaimMembershipThunkResponse,
    putRoleClaimMembership,
    PutRoleClaimMembershipThunkResponse
} from 'common/services/api/RoleClaimMembership/RoleClaimMembership.thunk';
import { Claim, Role } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { CLAIM_VALUE_NOT_CHANGED_ERROR_MESSAGE } from 'common/utils/claimErrorHandlers';
import { DrawerIDs } from 'common/utils/DrawerIDs';

import { ClaimNameDropDown } from './ClaimNameDropdown';

interface RoleClaimMembershipItem {
    claimValue: string;
    claimId: string;
}

interface RoleClaimMembershipDrawerProperties {
    tenantName: string;
    role: Role;
}

function useRoleClaimMembershipDrawer(
    entityName: string,
    drawerProps: RoleClaimMembershipDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<Claim & RoleClaimMembershipItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();

    const drawerContext = useDrawerContext<
        Claim & RoleClaimMembershipItem,
        RoleClaimMembershipDrawerProperties,
        APIRoleClaimMembershipPostParams,
        PostRoleClaimMembershipThunkResponse,
        APIRoleClaimMembershipPutParams,
        PutRoleClaimMembershipThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const claimMembership = useClaimMembershipDrawer({
        drawerId: DrawerIDs.Claims,
        currentId: drawerContext.currentId,
        isOpenDrawer: drawerContext.isOpenDrawer,
        action: drawerContext.action,
        editingClaimValue: editingItemInfo?.editingRow?.claimValue,
        entityType: 'Claim'
    });

    const getPostOptions = useCallback((): APIRoleClaimMembershipPostParams => ({
        tenantId: drawerProps.role.tenantId,
        roleId: drawerProps.role.id,
        claimId: claimMembership.claim.id,
        claimValue: claimMembership.claimValue,
    }), [drawerProps.role.tenantId, drawerProps.role.id, claimMembership.claim.id, claimMembership.claimValue]);

    const getPutOptions = useCallback((): APIRoleClaimMembershipPutParams => ({
        tenantId: drawerProps.role.tenantId,
        roleId: drawerProps.role.id,
        claimId: editingItemInfo?.editingRow?.claimId ?? '',
        claimValue: claimMembership.claimValue,
    }), [drawerProps.role.tenantId, drawerProps.role.id, editingItemInfo?.editingRow?.claimId, claimMembership.claimValue]);

    const createSuccessHandler = useCallback((postResult: PostRoleClaimMembershipThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutRoleClaimMembershipThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postRoleClaimMembership, getPostOptions, createSuccessHandler, claimMembership.createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, claimMembership.createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putRoleClaimMembership, getPutOptions, editSuccessHandler, claimMembership.editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, claimMembership.editFailureHandler]);

    return {
        ...drawerContext,
        claim: claimMembership.claim,
        claimValue: claimMembership.claimValue,
        valueInConflict: claimMembership.valueInConflict,
        handleClaimChange: claimMembership.handleClaimChange,
        handleClaimValueChange: claimMembership.handleClaimValueChange,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    claim,
    claimValue,
    handleClaimChange,
    handleClaimValueChange,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    claim: Claim;
    claimValue: string;
    handleClaimChange: (_e: unknown, newClaim: Claim) => void;
    handleClaimValueChange: (value: string) => void;
    postCallback: () => void;
    drawerProps: RoleClaimMembershipDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ClaimNameDropDown
                    value={claim}
                    onChange={handleClaimChange}
                    tenantId={drawerProps.role.tenantId}
                    hasError={nameInConflict}
                />
                {nameInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        This Claim is already linked to this Role.
                    </Box>
                }
                <ReadOnlyTextField label="Role Name" value={drawerProps.role.name} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
                <ClaimValueField
                    claim={claim}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    claimValue,
    valueInConflict,
    handleClaimValueChange,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    claimValue: string;
    valueInConflict: boolean;
    handleClaimValueChange: (value: string) => void;
    putCallback: () => void;
    drawerProps: RoleClaimMembershipDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<Claim & RoleClaimMembershipItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField label="Claim Name" value={editingItemInfo?.editingRow?.name ?? ''} />
                <ReadOnlyTextField label="Role Name" value={drawerProps.role.name ?? ''} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName ?? ''} />
                <ClaimValueField
                    claim={editingItemInfo?.editingRow}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                    hasError={valueInConflict}
                />
                {valueInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        {CLAIM_VALUE_NOT_CHANGED_ERROR_MESSAGE}
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

export function RoleClaimMembershipCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<Claim & RoleClaimMembershipItem>>) {
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerProps: RoleClaimMembershipDrawerProperties = {
        tenantName: pageInfo?.tenantName ?? '',
        role: {
            tenantId: pageInfo?.tenantId ?? '',
            id: pageInfo?.id ?? '',
            name: pageInfo?.name ?? ''
        }
    };

    const drawerState = useRoleClaimMembershipDrawer(title, drawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                claim={drawerState.claim}
                claimValue={drawerState.claimValue}
                handleClaimChange={drawerState.handleClaimChange}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                postCallback={drawerState.postCallback}
                drawerProps={drawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                claimValue={drawerState.claimValue}
                valueInConflict={drawerState.valueInConflict}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                putCallback={drawerState.putCallback}
                drawerProps={drawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
